import { AxiosResponse } from "axios";

import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "app-constants";
import * as models from "models";

import { pureApiAxios } from "./apiAxios";
import { ApiRequests, OSMRequests, PublicRequests } from "./request-utils";

/**
 * Use this when you need to specify other type arguments for dynamic url functions (DynamicGet, DynamicPost)
 * @example
 * Requests.DynamicPost<[string], models.RegisterPayload>((token) => `/api/register/${token}`)
 */

export const api = {
  auth: {
    registerRequest: {
      post: PublicRequests.Post<models.RegisterRequestPayload>("/main/business/access-request"),
      get: PublicRequests.DynamicGet<[string], models.RegisterRequestResponse>(
        (token) => `/main/business/access-request/${token}`,
      ),
    },

    login: async (credentials: models.LoginCredentials) => {
      const { data: tokens } = await pureApiAxios.post<models.LoginCredentials, AxiosResponse<models.TokensResponse>>(
        `/main/business/login`,
        credentials,
      );

      credentials.stay_signed_in && tokens?.refresh && localStorage.setItem(REFRESH_TOKEN_KEY, tokens?.refresh);
      tokens?.access && localStorage.setItem(ACCESS_TOKEN_KEY, tokens?.access);

      return tokens;
    },

    register: async (token: string, payload: models.RegisterPayload) => {
      const { data: transactionTokens } = await pureApiAxios.post<unknown, AxiosResponse<models.TransactionToken>>(
        `/main/business/register/${token}`,
        payload,
      );

      return transactionTokens;
    },

    registerComplete: async (transactionToken: string) => {
      const { data: tokens } = await pureApiAxios.post<unknown, AxiosResponse<models.TokensResponse>>(
        `/main/business/register-complete/${transactionToken}`,
      );
      tokens?.refresh && localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh);
      tokens?.access && localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access);
      return tokens;
    },

    refreshToken: async (refresh: string) => {
      const { data: tokens } = await pureApiAxios.post<models.TokensResponse>(`/main/business/refresh`, {
        refresh: refresh,
      });

      tokens?.refresh && localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh);
      tokens?.access && localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access);
    },

    logout: async () => {
      // Remove tokens from local storage
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    },
  },
  getHostedFieldToken: PublicRequests.Get<models.HostedFieldTokenResponse>("/main/business/payment/session-token"),
  cancelSubscription: ApiRequests.Post<models.CancelSubscriptionPayload>("/main/business/subscription/cancel"),
  renewSubscription: ApiRequests.Post<models.RenewSubscriptionPayload>("/main/business/subscription/renew"),
  getProfile: ApiRequests.Get<models.Profile>("/main/business/profile"),
  patchProfile: ApiRequests.DynamicPatch<[number], models.ProfilePatch, models.Profile>(
    (id) => `/main/business/profile/${id}`,
  ),
  search: PublicRequests.Get<unknown, models.SearchParams>("/business-api/search"),
  getAllPlans: PublicRequests.Get<models.Plan[], models.PlanQueryParams>("/main/business/plans"),
  payment: {
    buy: ApiRequests.Post<models.Buy, models.BuyResponse>("/main/business/payment/buy"),
    checkTransactions: ApiRequests.DynamicGet<[string], models.CheckTransactions>(
      (transactionId) => `/main/business/payment/transactions/${transactionId}/check`,
    ),
  },
  getOSMAddress: OSMRequests.Get<models.AddressOSM, models.QueryOSM>("/reverse", {
    additionalQuery: {
      format: "jsonv2",
    },
  }),
  resetToken: ApiRequests.Post<void>("/main/business/profile/reset-token"),
  checkServiceAvailability: PublicRequests.Get<models.ServiceAvailability>("/main/business/check-service-availability"),
  changeProfielInvoiceDetails: ApiRequests.Patch<models.UpdateProfile, models.Profile>(
    "/main/business/profile/change-invoice-details",
  ),
  getLastPaymentMethod: ApiRequests.Get<models.LastPaymentMethod>("/main/business/profile/last-payment-method"),
  resetPasswordRequest: ApiRequests.Post<models.ResetPasswordRequestPayload>(
    "/main/business/profile/reset-password-request",
  ),
  resetPasswordComplete: ApiRequests.Post<models.ResetPasswordCompletePayload>(
    "/main/business/profile/reset-password-complete",
  ),
  sendSMSVerificationCode: ApiRequests.Post<models.SendSMSVerificationPayload>("/main/business/otp/send"),
  verifySMSCode: ApiRequests.Post<models.PhoneVerificationCodePayload>("/main/business/otp/verify"),
};
